import { useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import type { SprintActivityWherebyMeetingRequest } from '../../../../../../pure-js/types/WherebyTypes'
import { useEnsureSprintActivityWherebyMeeting, useSprintActivityWherebyMeeting } from '../../../../apiLayer'
import { useWherebyCallOwnership } from '../../../../shared/hooks/useWherebyCallOwnership'
import { buildSprintCallScope } from '../../../../shared/hooks/wherebyCallScopes'
import { useGlobalWherebyFloatWatchdogs } from '../../components/whereby/useGlobalWherebyFloatWatchdogs'

/** The room of the call, this tab's claim on the person's one call, and opening the room when nobody has yet. */
export const useSprintCallRoom = (request: SprintActivityWherebyMeetingRequest | undefined) => {
  const location = useLocation()
  const roomQuery = useSprintActivityWherebyMeeting(request)
  const ensureMutation = useEnsureSprintActivityWherebyMeeting()
  const sprintCall = roomQuery.data?.data ?? undefined

  const ownershipScope = useMemo(
    () =>
      buildSprintCallScope({
        teamId: sprintCall?.teamId,
        sprintId: request?.sprintId,
        activityId: request?.activityId,
        activityType: sprintCall?.activityType,
        route: location.pathname
      }),
    [sprintCall?.teamId, sprintCall?.activityType, request, location.pathname]
  )

  const ownership = useWherebyCallOwnership({ enabled: Boolean(ownershipScope), scope: ownershipScope })

  const ensureRoom = useCallback(() => {
    if (request) ensureMutation.mutate(request)
  }, [request, ensureMutation])

  const refetchRoom = useCallback(() => void roomQuery.refetch(), [roomQuery])

  const watchdogs = useGlobalWherebyFloatWatchdogs({
    isCallOwnedByThisTab: ownership.canConnect,
    hasScope: Boolean(ownershipScope),
    roomUrl: sprintCall?.room?.roomUrl,
    scopeKey: request?.activityId,
    isEnsuringRoom: ensureMutation.isPending,
    ensureRoom,
    refetchRoom
  })

  const hasFailedToLoad = roomQuery.data?.success === false

  return { sprintCall, ownership, watchdogs, hasFailedToLoad }
}
